import React, { useState, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useTheme } from '@material-ui/core/styles';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Switch, FormControlLabel, Grid, Box,
  makeStyles, CircularProgress, Typography, Chip,
  Select, MenuItem, InputLabel, FormControl, IconButton,
  Paper
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import CodeIcon from '@material-ui/icons/Code';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import MenuBookIcon from '@material-ui/icons/MenuBook';

import {
  getHadithById,
  createHadith,
  updateHadith,
  getHadithBooks,
  addHadithBook,
  getHadithChapters,
  addHadithChapter
} from '../../services/hadithService';

const useStyles = makeStyles((theme) => ({
  dialogRoot: {
    '& .MuiDialog-paper': {
      margin: 24,
      width: 'calc(100vw - 48px)',
      maxWidth: 'calc(100vw - 48px) !important',
      height: 'calc(100vh - 48px)',
      maxHeight: 'calc(100vh - 48px)',
      borderRadius: 20,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      background: theme.palette.type === 'dark' ? '#0f172a' : '#ffffff',
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: '0 25px 70px rgba(0,0,0,0.35)',
      '@media (max-width: 600px)': {
        margin: 8,
        width: 'calc(100vw - 16px)',
        height: 'calc(100vh - 16px)',
        maxHeight: 'calc(100vh - 16px)',
        borderRadius: 14,
      },
    },
  },
  dialogHeader: {
    padding: '16px 24px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: theme.palette.type === 'dark' ? '#111827' : '#f8fafc',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: 'linear-gradient(135deg, rgba(16,185,129,0.2) 0%, rgba(5,150,105,0.2) 100%)',
    color: '#10b981',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: '1.2rem',
    color: theme.palette.text.primary,
    lineHeight: 1.2,
  },
  headerSubtitle: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  closeBtn: {
    color: theme.palette.text.secondary,
    borderRadius: 10,
    padding: 8,
    '&:hover': {
      backgroundColor: 'rgba(239,68,68,0.1)',
      color: '#ef4444',
    },
  },
  dialogContent: {
    padding: '24px 28px',
    overflowY: 'auto',
    flexGrow: 1,
    background: theme.palette.type === 'dark' ? '#0b1120' : '#f8fafc',
  },
  dialogActions: {
    padding: '14px 24px',
    borderTop: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === 'dark' ? '#111827' : '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  sectionCard: {
    padding: 20,
    borderRadius: 16,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: 20,
    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: theme.palette.text.primary,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  field: {
    marginBottom: theme.spacing(2.5),
    '& .MuiInputBase-root': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.5)' : '#ffffff',
      borderRadius: 10,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.secondary,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#10b981',
    },
  },
  arabicField: {
    '& .MuiInputBase-root': {
      direction: 'rtl',
      fontFamily: '"Traditional Arabic", "Scheherazade New", "Amiri", serif',
      fontSize: '1.45rem',
      lineHeight: 2.0,
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.5)' : '#ffffff',
    },
  },
  selectControl: {
    marginBottom: theme.spacing(2.5),
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      fontSize: '0.9rem',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#10b981',
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.5)' : '#ffffff',
      borderRadius: 10,
      color: theme.palette.text.primary,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.secondary,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#10b981',
    },
  },
  editorContainer: {
    marginBottom: theme.spacing(2.5),
    '& .ql-toolbar': {
      backgroundColor: theme.palette.type === 'dark' ? '#1e293b' : '#f1f5f9',
      borderColor: theme.palette.divider,
      borderTopLeftRadius: 10,
      borderTopRightRadius: 10,
      '& .ql-stroke': { stroke: theme.palette.text.secondary },
      '& .ql-fill': { fill: theme.palette.text.secondary },
      '& .ql-picker': { color: theme.palette.text.secondary },
    },
    '& .ql-container': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.4)' : '#ffffff',
      borderColor: theme.palette.divider,
      borderBottomLeftRadius: 10,
      borderBottomRightRadius: 10,
      minHeight: 140,
      fontFamily: theme.typography.fontFamily,
      fontSize: '0.95rem',
      color: theme.palette.text.primary,
    },
    '& .ql-editor.ql-blank::before': {
      color: theme.palette.text.hint,
    },
  },
  editorLabel: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    fontSize: '0.85rem',
    marginBottom: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  inlineCreateCard: {
    padding: 16,
    borderRadius: 12,
    background: theme.palette.type === 'dark' ? 'rgba(16,185,129,0.08)' : 'rgba(16,185,129,0.05)',
    border: '1px solid rgba(16,185,129,0.3)',
    marginBottom: 16,
  },
  chipListContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
    minHeight: 38,
    padding: 8,
    borderRadius: 8,
    background: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.4)' : '#f1f5f9',
    border: `1px dashed ${theme.palette.divider}`,
  },
  saveBtn: {
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#ffffff',
    fontWeight: 600,
    borderRadius: 10,
    padding: '8px 24px',
    boxShadow: '0 4px 14px rgba(16,185,129,0.3)',
    textTransform: 'none',
    '&:hover': {
      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    },
  },
  cancelBtn: {
    borderRadius: 10,
    color: theme.palette.text.secondary,
    textTransform: 'none',
    fontWeight: 500,
    padding: '8px 20px',
  },
}));

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'clean'],
  ],
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'link'
];

const HADITH_GRADES = [
  { label: 'সহিহ হাদিস (Sahih)', value: 'সহিহ হাদিস', color: '#10b981' },
  { label: 'হাসান (Hasan)', value: 'হাসান', color: '#3b82f6' },
  { label: 'দুর্বল / যঈফ (Da\'if)', value: 'দুর্বল', color: '#f59e0b' },
  { label: 'জাল / মাওদূ (Mawdu)', value: 'জাল / মাওদূ', color: '#ef4444' },
  { label: 'মুতাওয়াতির (Mutawatir)', value: 'মুতাওয়াতির', color: '#8b5cf6' },
  { label: 'অন্যান্য (Custom)', value: 'Custom', color: '#64748b' },
];

const DEFAULT_BOOKS = [
  { id: 'bukhari', nameEn: 'Sahih al-Bukhari', nameBn: 'সহীহ বুখারী', author: 'Imam Bukhari' },
  { id: 'muslim', nameEn: 'Sahih Muslim', nameBn: 'সহীহ মুসলিম', author: 'Imam Muslim' },
  { id: 'nasai', nameEn: 'Sunan an-Nasai', nameBn: 'সুনানে আন-নাসায়ী', author: 'Imam an-Nasai' },
  { id: 'abudawud', nameEn: 'Sunan Abu Dawud', nameBn: 'সুনান আবু দাউদ', author: 'Imam Abu Dawud' },
  { id: 'tirmidhi', nameEn: "Jami' at-Tirmidhi", nameBn: 'জামে আত-তিরমিযী', author: 'Imam at-Tirmidhi' },
  { id: 'ibnmajah', nameEn: 'Sunan Ibn Majah', nameBn: 'সুনানে ইবনে মাজাহ', author: 'Imam Ibn Majah' },
  { id: 'riyadus_salihin', nameEn: 'Riyadus Salihin', nameBn: 'রিয়াদুস সালেহীন', author: 'Imam an-Nawawi' },
  { id: '40_hadith', nameEn: '40 Hadith of Imam Nawawi', nameBn: '৪০ হাদিস (ইমাম নববী)', author: 'Imam an-Nawawi' },
  { id: '100_hadith', nameEn: '100 Short Hadith', nameBn: '১০০ ক্ষুদ্র হাদিস', author: '' },
  { id: 'upodesh', nameEn: 'Hadither Upodesh', nameBn: 'হাদিসের উপদেশ', author: '' },
  { id: 'sahih_hadise_qudsi', nameEn: 'Sahih Hadise Qudsi', nameBn: 'সহীহ হাদীসে কুদসী', author: '' },
];

const INITIAL_FORM = {
  textArabic: '',
  arabic: '',
  titleBn: '',
  titleEn: '',
  translationBn: '',
  translationEn: '',
  explanationBn: '',
  explanationEn: '',
  narrator: '',
  grade: 'সহিহ হাদিস',
  gradeColor: '#10b981',
  bookSlug: 'bukhari',
  chapter: '',
  chapterId: 1,
  hadithNumber: '',
  referenceNumber: '',
  reference: '',
  teachings: [],
  note: '',
  sourceUrl: '',
  sourceName: 'iHadis',
  sourceWeb: 'ihadis.com',
  sourceWebUrl: 'https://ihadis.com',
  tags: [],
  isPublished: true,
};

export default function HadithFormModal({
  open,
  onClose,
  hadithId = null,
  onSuccess,
}) {
  const classes = useStyles();
  const theme = useTheme();

  const isEdit = Boolean(hadithId);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form State
  const [form, setForm] = useState(INITIAL_FORM);
  const [books, setBooks] = useState(DEFAULT_BOOKS);
  const [chapters, setChapters] = useState([]);
  const [loadingChapters, setLoadingChapters] = useState(false);

  // Inline Book Creator
  const [showAddBook, setShowAddBook] = useState(false);
  const [newBook, setNewBook] = useState({ id: '', nameEn: '', nameBn: '', author: '' });

  // Inline Chapter Creator
  const [showAddChapter, setShowAddChapter] = useState(false);
  const [newChapter, setNewChapter] = useState({ chapterId: '', titleBn: '', titleEn: '' });

  // HTML Editor Toggles
  const [showHTML, setShowHTML] = useState({
    transBn: false,
    transEn: false,
    expBn: false,
    expEn: false,
  });

  // Teaching input & Tag input
  const [teachingInput, setTeachingInput] = useState('');
  const [tagInput, setTagInput] = useState('');

  // Validation Error message
  const [errorMsg, setErrorMsg] = useState('');

  // 1. Fetch Books
  useEffect(() => {
    let mounted = true;
    getHadithBooks().then((dbBooks) => {
      if (!mounted) return;
      const map = new Map();
      DEFAULT_BOOKS.forEach((b) => map.set(b.id, b));
      dbBooks.forEach((b) => {
        map.set(b.id, { ...(map.get(b.id) || {}), ...b });
      });
      setBooks(Array.from(map.values()));
    });
    return () => { mounted = false; };
  }, []);

  // 2. Fetch Chapters when book changes
  const loadBookChapters = useCallback(async (slug) => {
    if (!slug) return;
    setLoadingChapters(true);
    try {
      const chs = await getHadithChapters(slug);
      setChapters(chs || []);
    } catch (e) {
      console.warn('Failed to load chapters for book:', slug, e);
      setChapters([]);
    }
    setLoadingChapters(false);
  }, []);

  useEffect(() => {
    if (form.bookSlug) {
      loadBookChapters(form.bookSlug);
    }
  }, [form.bookSlug, loadBookChapters]);

  // 3. Load Hadith data if Edit Mode
  useEffect(() => {
    if (!open) return;
    if (hadithId) {
      setLoading(true);
      setErrorMsg('');
      getHadithById(hadithId)
        .then((doc) => {
          if (!doc) {
            setErrorMsg('Hadith record not found.');
            setLoading(false);
            return;
          }
          const bSlug = doc.bookSlug || doc.bookId || doc.categoryId || 'bukhari';
          const arText = doc.textArabic || doc.arabic || '';
          const tBn = doc.translationBn || doc.bengali || doc.translation?.bn || '';
          const tEn = doc.translationEn || doc.english || doc.translation?.en || '';
          const titBn = doc.titleBn || doc.title?.bn || '';
          const titEn = doc.titleEn || doc.title?.en || '';
          const eBn = doc.explanationBn || doc.explanation?.bn || '';
          const eEn = doc.explanationEn || doc.explanation?.en || '';
          const hNum = doc.hadithNumber || doc.referenceNumber || '';
          const chId = doc.chapterId || 1;
          const chName = doc.chapter || '';

          setForm({
            ...INITIAL_FORM,
            ...doc,
            bookSlug: bSlug,
            textArabic: arText,
            arabic: arText,
            titleBn: titBn,
            titleEn: titEn,
            translationBn: tBn,
            translationEn: tEn,
            explanationBn: eBn,
            explanationEn: eEn,
            narrator: doc.narrator || '',
            grade: doc.grade || 'সহিহ হাদিস',
            gradeColor: doc.gradeColor || '#10b981',
            chapter: chName,
            chapterId: chId,
            hadithNumber: hNum,
            referenceNumber: doc.referenceNumber || String(hNum),
            reference: doc.reference || '',
            teachings: Array.isArray(doc.teachings) ? doc.teachings : [],
            note: doc.note || '',
            sourceUrl: doc.sourceUrl || '',
            sourceName: doc.sourceName || 'iHadis',
            sourceWeb: doc.sourceWeb || 'ihadis.com',
            sourceWebUrl: doc.sourceWebUrl || 'https://ihadis.com',
            tags: Array.isArray(doc.tags) ? doc.tags : [],
            isPublished: doc.isPublished !== undefined ? doc.isPublished : true,
          });
          setLoading(false);
        })
        .catch((err) => {
          console.error('Error fetching hadith:', err);
          setErrorMsg('Failed to load Hadith details.');
          setLoading(false);
        });
    } else {
      // Create mode reset
      setForm(INITIAL_FORM);
      setErrorMsg('');
      setLoading(false);
    }
  }, [open, hadithId]);

  // Handle Book Change
  const handleBookChange = (slug) => {
    if (slug === 'CREATE_NEW') {
      setShowAddBook(true);
      return;
    }
    const bookObj = books.find((b) => b.id === slug);
    const bookTitle = bookObj ? (bookObj.nameEn || bookObj.name) : slug;
    const hNum = form.hadithNumber || 1;

    setForm((prev) => ({
      ...prev,
      bookSlug: slug,
      source: bookTitle,
      sourceBn: bookObj?.nameBn || bookTitle,
      sourceUrl: `https://ihadis.com/${slug}/${form.chapterId || 1}`,
      reference: `${bookTitle} ${hNum}`,
    }));
  };

  // Handle Chapter Change
  const handleChapterSelect = (selectedChId) => {
    if (selectedChId === 'CREATE_NEW') {
      setShowAddChapter(true);
      return;
    }
    const chObj = chapters.find((c) => String(c.chapterId) === String(selectedChId));
    setForm((prev) => ({
      ...prev,
      chapterId: Number(selectedChId),
      chapter: chObj ? (chObj.titleBn || chObj.title) : prev.chapter,
      subCategoryId: chObj?.id || `${prev.bookSlug}_ch_${selectedChId}`,
      sourceUrl: `https://ihadis.com/${prev.bookSlug}/${selectedChId}`,
    }));
  };

  // Create Book inline
  const handleSaveNewBook = async () => {
    if (!newBook.nameEn.trim()) return;
    const id = (newBook.id.trim() || newBook.nameEn.trim().toLowerCase().replace(/[^a-z0-9]/g, '')) || 'custom_book';
    const bookData = {
      id,
      slug: id,
      name: newBook.nameEn.trim(),
      nameEn: newBook.nameEn.trim(),
      nameBn: newBook.nameBn.trim() || newBook.nameEn.trim(),
      author: newBook.author.trim(),
    };
    try {
      await addHadithBook(id, bookData.nameEn, bookData.nameBn, { author: bookData.author });
      setBooks((prev) => [...prev.filter((b) => b.id !== id), bookData]);
      setForm((prev) => ({
        ...prev,
        bookSlug: id,
        source: bookData.nameEn,
        sourceBn: bookData.nameBn,
        sourceUrl: `https://ihadis.com/${id}/1`,
      }));
      setShowAddBook(false);
      setNewBook({ id: '', nameEn: '', nameBn: '', author: '' });
    } catch (e) {
      console.error('Failed to create book:', e);
      setErrorMsg('Failed to create new book.');
    }
  };

  // Create Chapter inline
  const handleSaveNewChapter = async () => {
    if (!newChapter.titleBn.trim() && !newChapter.titleEn.trim()) return;
    const chNum = Number(newChapter.chapterId) || (chapters.length + 1);
    const chapterData = {
      bookSlug: form.bookSlug,
      chapterId: chNum,
      title: newChapter.titleBn.trim() || newChapter.titleEn.trim(),
      titleBn: newChapter.titleBn.trim() || newChapter.titleEn.trim(),
      titleEn: newChapter.titleEn.trim() || newChapter.titleBn.trim(),
    };
    try {
      const created = await addHadithChapter(chapterData);
      setChapters((prev) => [...prev, created]);
      setForm((prev) => ({
        ...prev,
        chapterId: chNum,
        chapter: created.titleBn,
        subCategoryId: created.id,
        sourceUrl: `https://ihadis.com/${prev.bookSlug}/${chNum}`,
      }));
      setShowAddChapter(false);
      setNewChapter({ chapterId: '', titleBn: '', titleEn: '' });
    } catch (e) {
      console.error('Failed to create chapter:', e);
      setErrorMsg('Failed to create new chapter.');
    }
  };

  // Tag Handlers
  const handleAddTag = () => {
    const val = tagInput.trim();
    if (val && !form.tags.includes(val)) {
      setForm((prev) => ({ ...prev, tags: [...prev.tags, val] }));
    }
    setTagInput('');
  };

  const handleRemoveTag = (tag) => {
    setForm((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  };

  // Teaching Handlers
  const handleAddTeaching = () => {
    const val = teachingInput.trim();
    if (val && !form.teachings.includes(val)) {
      setForm((prev) => ({ ...prev, teachings: [...prev.teachings, val] }));
    }
    setTeachingInput('');
  };

  const handleRemoveTeaching = (idx) => {
    setForm((prev) => ({ ...prev, teachings: prev.teachings.filter((_, i) => i !== idx) }));
  };

  // Save / Submit
  const handleSave = async () => {
    if (!form.textArabic.trim()) {
      setErrorMsg('আরবি মূল পাঠ (Arabic Text) আবশ্যক।');
      return;
    }
    if (!form.translationBn.trim() && !form.translationEn.trim()) {
      setErrorMsg('কমপক্ষে বাংলা বা ইংরেজি অনুবাদ পূরণ করুন।');
      return;
    }

    setSaving(true);
    setErrorMsg('');
    try {
      const bookObj = books.find((b) => b.id === form.bookSlug);
      const bookNameEn = bookObj?.nameEn || bookObj?.name || form.bookSlug;
      const bookNameBn = bookObj?.nameBn || bookNameEn;
      const hadithNum = form.hadithNumber ? Number(form.hadithNumber) : 1;

      const payload = {
        ...form,
        source: form.source || bookNameEn,
        sourceBn: form.sourceBn || bookNameBn,
        bookSlug: form.bookSlug,
        bookId: form.bookSlug,
        categoryId: form.bookSlug,
        hadithNumber: hadithNum,
        referenceNumber: form.referenceNumber || String(hadithNum),
        reference: form.reference || `${bookNameEn} ${hadithNum}`,
        arabic: form.textArabic,
        textArabic: form.textArabic,
        title: { bn: form.titleBn, en: form.titleEn },
        translation: { bn: form.translationBn, en: form.translationEn },
        explanation: { bn: form.explanationBn, en: form.explanationEn },
      };

      let result;
      if (isEdit) {
        await updateHadith(hadithId, payload);
        result = { id: hadithId, ...payload };
      } else {
        result = await createHadith(payload);
      }

      setSaving(false);
      if (onSuccess) onSuccess(result);
      onClose();
    } catch (err) {
      console.error('Error saving hadith:', err);
      setErrorMsg(err?.message || 'Failed to save Hadith.');
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      className={classes.dialogRoot}
      disableBackdropClick={saving}
      disableEscapeKeyDown={saving}
      scroll="paper"
    >
      {/* Top Header: Left Title, Right Close */}
      <DialogTitle disableTypography className={classes.dialogHeader}>
        <div className={classes.headerLeft}>
          <div className={classes.headerIcon}>
            <MenuBookIcon />
          </div>
          <div>
            <Typography className={classes.headerTitle}>
              {isEdit ? 'হাদিস সম্পাদনা (Edit Hadith Record)' : 'নতুন হাদিস যোগ করুন (Add New Hadith)'}
            </Typography>
            <Typography className={classes.headerSubtitle}>
              {isEdit
                ? `ID: ${hadithId} • ${form.reference || 'Hadith'}`
                : 'সকল প্রয়োজনীয় তথ্য ও গ্রন্থ বিন্যাস সহ নতুন হাদিস সংরক্ষণ করুন'}
            </Typography>
          </div>
        </div>

        <IconButton
          onClick={onClose}
          className={classes.closeBtn}
          size="small"
          disabled={saving}
          title="Close Modal"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Main Body */}
      <DialogContent className={classes.dialogContent} dividers>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={350}>
            <CircularProgress style={{ color: '#10b981' }} size={48} thickness={4} />
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
              হাদিসের তথ্য লোড হচ্ছে...
            </Typography>
          </Box>
        ) : (
          <>
            {errorMsg && (
              <Box mb={2.5} p={1.5} borderRadius={8} bgcolor="rgba(239,68,68,0.1)" border="1px solid rgba(239,68,68,0.3)">
                <Typography variant="body2" style={{ color: '#ef4444', fontWeight: 600 }}>
                  <span role="img" aria-label="error">⚠️</span> {errorMsg}
                </Typography>
              </Box>
            )}

            {/* SECTION 1: Book & Chapter Hierarchy */}
            <Paper elevation={0} className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>
                <MenuBookIcon fontSize="small" style={{ color: '#10b981' }} />
                গ্রন্থ ও অধ্যায় বিন্যাস (Book &amp; Chapter Hierarchy)
              </Typography>

              <Grid container spacing={3}>
                {/* Book Selection */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" className={classes.selectControl}>
                    <InputLabel id="hadith-book-label">কিতাব / গ্রন্থ (Hadith Book)</InputLabel>
                    <Select
                      labelId="hadith-book-label"
                      value={form.bookSlug}
                      onChange={(e) => handleBookChange(e.target.value)}
                      label="কিতাব / গ্রন্থ (Hadith Book)"
                    >
                      {books.map((b) => (
                        <MenuItem key={b.id} value={b.id}>
                          {b.nameEn || b.name} {b.nameBn ? `(${b.nameBn})` : ''}
                        </MenuItem>
                      ))}
                      <MenuItem
                        value="CREATE_NEW"
                        style={{
                          borderTop: `1px solid ${theme.palette.divider}`,
                          marginTop: 4,
                          paddingTop: 8,
                          color: '#10b981',
                          fontWeight: 700,
                        }}
                      >
                        + নতুন কিতাব তৈরি করুন (Add New Book)
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {/* Inline Book Creation Form */}
                  {showAddBook && (
                    <Box className={classes.inlineCreateCard}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#10b981' }}>
                          নতুন কিতাব যোগ করুন (Create New Book)
                        </Typography>
                        <IconButton size="small" onClick={() => setShowAddBook(false)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="Book Name (English)"
                            placeholder="e.g. Musnad Ahmad"
                            value={newBook.nameEn}
                            onChange={(e) => setNewBook({ ...newBook, nameEn: e.target.value })}
                            className={classes.field}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="গ্রন্থের নাম (বাংলা)"
                            placeholder="যেমন: মুসনাদে আহমাদ"
                            value={newBook.nameBn}
                            onChange={(e) => setNewBook({ ...newBook, nameBn: e.target.value })}
                            className={classes.field}
                          />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                          <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="সংকলক / লেখক (Author)"
                            placeholder="যেমন: ইমাম আহমাদ বিন হাম্বল"
                            value={newBook.author}
                            onChange={(e) => setNewBook({ ...newBook, author: e.target.value })}
                            className={classes.field}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            onClick={handleSaveNewBook}
                            disabled={!newBook.nameEn.trim()}
                            style={{
                              height: 40,
                              borderRadius: 10,
                              background: '#10b981',
                              color: '#fff',
                              textTransform: 'none',
                              fontWeight: 600,
                            }}
                          >
                            Save Book
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </Grid>

                {/* Chapter Selection */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" className={classes.selectControl}>
                    <InputLabel id="hadith-chapter-label">
                      {loadingChapters ? 'অধ্যায় লোড হচ্ছে...' : 'অধ্যায় / পরিচ্ছেদ (Chapter)'}
                    </InputLabel>
                    <Select
                      labelId="hadith-chapter-label"
                      value={form.chapterId || ''}
                      onChange={(e) => handleChapterSelect(e.target.value)}
                      label={loadingChapters ? 'অধ্যায় লোড হচ্ছে...' : 'অধ্যায় / পরিচ্ছেদ (Chapter)'}
                    >
                      <MenuItem value="">
                        <em>অধ্যায় নির্বাচন করুন...</em>
                      </MenuItem>
                      {chapters.map((ch) => (
                        <MenuItem key={ch.id || ch.chapterId} value={ch.chapterId}>
                          {ch.chapterId}. {ch.titleBn || ch.title || ch.titleEn}
                        </MenuItem>
                      ))}
                      <MenuItem
                        value="CREATE_NEW"
                        style={{
                          borderTop: `1px solid ${theme.palette.divider}`,
                          marginTop: 4,
                          paddingTop: 8,
                          color: '#10b981',
                          fontWeight: 700,
                        }}
                      >
                        + নতুন অধ্যায় তৈরি করুন (Add New Chapter)
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {/* Inline Chapter Creation Form */}
                  {showAddChapter && (
                    <Box className={classes.inlineCreateCard}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2" style={{ fontWeight: 700, color: '#10b981' }}>
                          নতুন অধ্যায় যোগ করুন (Create New Chapter)
                        </Typography>
                        <IconButton size="small" onClick={() => setShowAddChapter(false)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            variant="outlined"
                            label="Chapter No."
                            placeholder="e.g. 1"
                            value={newChapter.chapterId}
                            onChange={(e) => setNewChapter({ ...newChapter, chapterId: e.target.value })}
                            className={classes.field}
                          />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                          <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="অধ্যায়ের নাম (বাংলা)"
                            placeholder="যেমন: ঈমান অধ্যায়"
                            value={newChapter.titleBn}
                            onChange={(e) => setNewChapter({ ...newChapter, titleBn: e.target.value })}
                            className={classes.field}
                          />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                          <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            label="Chapter Name (English)"
                            placeholder="e.g. Book of Faith"
                            value={newChapter.titleEn}
                            onChange={(e) => setNewChapter({ ...newChapter, titleEn: e.target.value })}
                            className={classes.field}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            onClick={handleSaveNewChapter}
                            disabled={!newChapter.titleBn.trim() && !newChapter.titleEn.trim()}
                            style={{
                              height: 40,
                              borderRadius: 10,
                              background: '#10b981',
                              color: '#fff',
                              textTransform: 'none',
                              fontWeight: 600,
                            }}
                          >
                            Save Chapter
                          </Button>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </Grid>

                {/* Hadith Number & Reference */}
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="হাদিস নম্বর (Hadith Number)"
                    placeholder="e.g. 1"
                    value={form.hadithNumber}
                    onChange={(e) => {
                      const val = e.target.value;
                      const bookObj = books.find((b) => b.id === form.bookSlug);
                      const bName = bookObj?.nameEn || bookObj?.name || 'Hadith';
                      setForm((prev) => ({
                        ...prev,
                        hadithNumber: val,
                        referenceNumber: val,
                        reference: `${bName} ${val}`,
                      }));
                    }}
                    className={classes.field}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="রেফারেন্স নম্বর (Reference Number)"
                    placeholder="e.g. 1"
                    value={form.referenceNumber}
                    onChange={(e) => setForm({ ...form, referenceNumber: e.target.value })}
                    className={classes.field}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="পূর্ণ রেফারেন্স (Reference String)"
                    placeholder="e.g. Sahih al-Bukhari 1"
                    value={form.reference}
                    onChange={(e) => setForm({ ...form, reference: e.target.value })}
                    className={classes.field}
                  />
                </Grid>

                {/* Hadith Authenticity / Grade */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" className={classes.selectControl}>
                    <InputLabel id="grade-select-label">হাদিসের মান (Grade / Authenticity)</InputLabel>
                    <Select
                      labelId="grade-select-label"
                      value={HADITH_GRADES.some((g) => g.value === form.grade) ? form.grade : 'Custom'}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === 'Custom') {
                          setForm({ ...form, grade: '', gradeColor: '#64748b' });
                        } else {
                          const matched = HADITH_GRADES.find((g) => g.value === val);
                          setForm({
                            ...form,
                            grade: val,
                            gradeColor: matched ? matched.color : '#10b981',
                          });
                        }
                      }}
                      label="হাদিসের মান (Grade / Authenticity)"
                    >
                      {HADITH_GRADES.map((g) => (
                        <MenuItem key={g.value} value={g.value}>
                          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                            <Box
                              width={10}
                              height={10}
                              borderRadius="50%"
                              style={{ backgroundColor: g.color }}
                            />
                            <span>{g.label}</span>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {!HADITH_GRADES.some((g) => g.value === form.grade) && (
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      label="Custom Grade"
                      placeholder="e.g. হাসান সহিহ"
                      value={form.grade}
                      onChange={(e) => setForm({ ...form, grade: e.target.value })}
                      className={classes.field}
                    />
                  )}
                </Grid>

                {/* Narrator (বর্ণনাকারী) */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="বর্ণনাকারী / রাবী (Narrator)"
                    placeholder="যেমন: ‘আলক্বামাহ ইব্‌নু ওয়াক্কাস আল-লায়সী (রহঃ)"
                    value={form.narrator}
                    onChange={(e) => setForm({ ...form, narrator: e.target.value })}
                    className={classes.field}
                    helperText="হাদিসটি কোন সাহাবী বা রাবী কর্তৃক বর্ণিত"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* SECTION 2: Arabic Matn */}
            <Paper elevation={0} className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>
                <span style={{ fontSize: '1.2rem', color: '#10b981' }}>۞</span>
                আরবি মূল পাঠ (Arabic Text / متن الحديث)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                label="মূল আরবি হাদিস (আরবি ফন্ট ও হরকত সহ)"
                placeholder="حَدَّثَنَا عَبْدُ اللَّهِ بْنُ الزُّبَيْرِ..."
                value={form.textArabic}
                onChange={(e) => setForm({ ...form, textArabic: e.target.value, arabic: e.target.value })}
                className={`${classes.field} ${classes.arabicField}`}
                required
              />
            </Paper>

            {/* SECTION 3: Titles */}
            <Paper elevation={0} className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>
                হাদিসের শিরোনাম (Hadith Titles)
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="বাংলা শিরোনাম (Bangla Title)"
                    placeholder="যেমন: ওহীর সূচনা অধ্যায়"
                    value={form.titleBn}
                    onChange={(e) => setForm({ ...form, titleBn: e.target.value })}
                    className={classes.field}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="English Title"
                    placeholder="e.g. Sahih al-Bukhari - Hadith 1"
                    value={form.titleEn}
                    onChange={(e) => setForm({ ...form, titleEn: e.target.value })}
                    className={classes.field}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* SECTION 4: Translations (Bangla & English) */}
            <Paper elevation={0} className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>
                অনুবাদ (Translations)
              </Typography>

              {/* Bangla Translation */}
              <Box className={classes.editorContainer}>
                <Box className={classes.editorLabel}>
                  <span>বাংলা অনুবাদ (Bengali Translation) *</span>
                  <IconButton
                    size="small"
                    onClick={() => setShowHTML((prev) => ({ ...prev, transBn: !prev.transBn }))}
                    title="Toggle HTML source"
                  >
                    <CodeIcon fontSize="small" />
                  </IconButton>
                </Box>
                {showHTML.transBn ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    variant="outlined"
                    value={form.translationBn}
                    onChange={(e) => setForm({ ...form, translationBn: e.target.value })}
                    className={classes.field}
                    placeholder="HTML কোড এডিট করুন..."
                  />
                ) : (
                  <ReactQuill
                    theme="snow"
                    value={form.translationBn}
                    onChange={(val) => setForm({ ...form, translationBn: val })}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="বাংলায় সুন্দর অনুবাদ লিখুন..."
                  />
                )}
              </Box>

              {/* English Translation */}
              <Box className={classes.editorContainer} style={{ marginTop: 20 }}>
                <Box className={classes.editorLabel}>
                  <span>English Translation</span>
                  <IconButton
                    size="small"
                    onClick={() => setShowHTML((prev) => ({ ...prev, transEn: !prev.transEn }))}
                    title="Toggle HTML source"
                  >
                    <CodeIcon fontSize="small" />
                  </IconButton>
                </Box>
                {showHTML.transEn ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    variant="outlined"
                    value={form.translationEn}
                    onChange={(e) => setForm({ ...form, translationEn: e.target.value })}
                    className={classes.field}
                    placeholder="Edit HTML source..."
                  />
                ) : (
                  <ReactQuill
                    theme="snow"
                    value={form.translationEn}
                    onChange={(val) => setForm({ ...form, translationEn: val })}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Write English translation here..."
                  />
                )}
              </Box>
            </Paper>

            {/* SECTION 5: Explanation & Key Teachings */}
            <Paper elevation={0} className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>
                হাদিসের তাৎপর্য, ব্যাখ্যা ও শিক্ষা (Explanation &amp; Teachings)
              </Typography>

              {/* Bangla Explanation */}
              <Box className={classes.editorContainer}>
                <Box className={classes.editorLabel}>
                  <span>হাদিসের তাৎপর্য ও ব্যাখ্যা (Bangla Explanation)</span>
                  <IconButton
                    size="small"
                    onClick={() => setShowHTML((prev) => ({ ...prev, expBn: !prev.expBn }))}
                    title="Toggle HTML source"
                  >
                    <CodeIcon fontSize="small" />
                  </IconButton>
                </Box>
                {showHTML.expBn ? (
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    variant="outlined"
                    value={form.explanationBn}
                    onChange={(e) => setForm({ ...form, explanationBn: e.target.value })}
                    className={classes.field}
                    placeholder="HTML কোড এডিট করুন..."
                  />
                ) : (
                  <ReactQuill
                    theme="snow"
                    value={form.explanationBn}
                    onChange={(val) => setForm({ ...form, explanationBn: val })}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="হাদিসের ব্যাখ্যা ও মূল বিষয়বস্তু লিখুন..."
                  />
                )}
              </Box>

              {/* Teachings List */}
              <Box mt={3}>
                <Typography variant="subtitle2" style={{ fontWeight: 600, color: theme.palette.text.secondary, marginBottom: 8 }}>
                  শিক্ষণীয় বিষয়সমূহ (Key Teachings / Lessons):
                </Typography>
                <Box display="flex" style={{ gap: 8 }} mb={1}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder="হাদিস থেকে শিক্ষণীয় একটি পয়েন্ট লিখে এন্টার দিন..."
                    value={teachingInput}
                    onChange={(e) => setTeachingInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTeaching())}
                    className={classes.field}
                    style={{ marginBottom: 0 }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddTeaching}
                    disabled={!teachingInput.trim()}
                    style={{
                      borderRadius: 10,
                      background: '#10b981',
                      color: '#fff',
                      textTransform: 'none',
                      padding: '0 16px',
                    }}
                  >
                    Add
                  </Button>
                </Box>
                <Box className={classes.chipListContainer}>
                  {form.teachings.length === 0 ? (
                    <Typography variant="caption" color="textSecondary" style={{ padding: 6 }}>
                      কোন শিক্ষণীয় পয়েন্ট যোগ করা হয়নি (যেমন: &apos;আমলের মূল্য নিয়তের উপর নির্ভরশীল&apos;)
                    </Typography>
                  ) : (
                    form.teachings.map((pt, idx) => (
                      <Chip
                        key={idx}
                        label={`${idx + 1}. ${pt}`}
                        onDelete={() => handleRemoveTeaching(idx)}
                        size="small"
                        style={{
                          backgroundColor: 'rgba(16,185,129,0.12)',
                          color: '#059669',
                          fontWeight: 500,
                        }}
                      />
                    ))
                  )}
                </Box>
              </Box>
            </Paper>

            {/* SECTION 6: Source & Metadata */}
            <Paper elevation={0} className={classes.sectionCard}>
              <Typography className={classes.sectionTitle}>
                উৎস ও প্রাসঙ্গিক তথ্য (Source Attribution &amp; Notes)
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="উৎস লিঙ্ক / সোর্স URL (e.g. https://ihadis.com/bukhari/1)"
                    value={form.sourceUrl}
                    onChange={(e) => setForm({ ...form, sourceUrl: e.target.value })}
                    className={classes.field}
                    InputProps={{
                      endAdornment: form.sourceUrl ? (
                        <IconButton
                          size="small"
                          component="a"
                          href={form.sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open Link"
                          style={{ color: '#10b981' }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      ) : null,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="সোর্স লেবেল (Source Name)"
                    value={form.sourceName}
                    onChange={(e) => setForm({ ...form, sourceName: e.target.value })}
                    className={classes.field}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="নোট ও অন্যান্য কিতাবের নম্বর (Takhrij / Notes)"
                    placeholder="যেমন: (মুসলিম ২৩/৪৫ হাঃ ১৯০৭, আহমাদ ১৬৮)"
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    className={classes.field}
                  />
                </Grid>

                {/* Tags */}
                <Grid item xs={12}>
                  <Box display="flex" style={{ gap: 8 }} mb={1}>
                    <TextField
                      fullWidth
                      size="small"
                      variant="outlined"
                      label="ট্যাগসমূহ (Tags)"
                      placeholder="ট্যাগ লিখে Enter চাপুন..."
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                      className={classes.field}
                      style={{ marginBottom: 0 }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddTag}
                      disabled={!tagInput.trim()}
                      style={{
                        borderRadius: 10,
                        background: '#10b981',
                        color: '#fff',
                        textTransform: 'none',
                        padding: '0 16px',
                      }}
                    >
                      Add Tag
                    </Button>
                  </Box>
                  <Box className={classes.chipListContainer}>
                    {form.tags.length === 0 ? (
                      <Typography variant="caption" color="textSecondary" style={{ padding: 6 }}>
                        কোন ট্যাগ যোগ করা হয়নি
                      </Typography>
                    ) : (
                      form.tags.map((tag) => (
                        <Chip
                          key={tag}
                          label={tag}
                          onDelete={() => handleRemoveTag(tag)}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </>
        )}
      </DialogContent>

      {/* Bottom Sticky Action Bar */}
      <DialogActions className={classes.dialogActions}>
        <FormControlLabel
          control={
            <Switch
              checked={form.isPublished}
              onChange={(e) => setForm({ ...form, isPublished: e.target.checked })}
              color="primary"
            />
          }
          label={
            <Typography variant="body2" style={{ fontWeight: 600, color: theme.palette.text.primary }}>
              {form.isPublished ? 'অবস্থা: প্রকাশিত (Published)' : 'অবস্থা: ড্রাফট (Draft)'}
            </Typography>
          }
        />

        <Box display="flex" alignItems="center" style={{ gap: 16 }}>
          <Button
            onClick={onClose}
            className={classes.cancelBtn}
            disabled={saving}
            style={{ marginRight: 8 }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            className={classes.saveBtn}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? 'সংরক্ষণ হচ্ছে...' : (isEdit ? 'আপডেট করুন (Save Changes)' : 'হাদিস প্রকাশ করুন (Publish Hadith)')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
